/**
 * External dependencies.
 */
import { useState, useEffect, useCallback, createInterpolateElement } from '@wordpress/element';
import { useSelect, useDispatch } from '@wordpress/data';
import { decodeEntities } from '@wordpress/html-entities';
import { __, sprintf } from '@wordpress/i18n';

/**
 * Internal dependencies.
 */
import Alert from '../../common/Alert';
import { getDataFromGlobalVars, isTimeslotMandatory, isTimeslotEnabled, dateYmdToDate, getShippingMethodLabelType } from '../../common/helper';
import { format } from 'date-fns';
import WdsDatePicker from '../../common/DatePicker/WdsDatePicker';
import { fetchTimeslotsOnDate } from '../../common/api';
import { SUBSCRIPTIONS_STORE_NAME as STORE_NAME } from '../../constants';
import SubscriptionProductType from '../../valueObject/SubscriptionProductType';
import { getMaxDate } from '../subscriptionHelper';

const SubscriptionMonthlySelector = ({subscription: subscriptionData, type}) => {
	const [date, setDate] = useState(null);
	const timeslot = useSelect(select => select(STORE_NAME).getTimeslot(type));
	const reservedSlot = useSelect(select => select(STORE_NAME).getReservedSlot(type));
	const dateFormat = getDataFromGlobalVars( 'date_fns_format' );
	const [timeslotOptions, setTimeslotOptions] = useState([
		{ value: '', formatted_with_fee: 'Loading...' },
	]);
	const [loadingTimeslots, setLoadingTimeslots] = useState(false);
	const shippingMethod = useSelect(select => select(STORE_NAME).getShippingMethod(type));
	const shippingMethodLabelType = getShippingMethodLabelType(shippingMethod);

	// Redux getters and setters
	const bookableDates = useSelect(select => select(STORE_NAME).getBookableDates(type));
	const { setDateYmd } = useDispatch(STORE_NAME);
	const { setTimeslot } = useDispatch(STORE_NAME);
	
	// Settings
	const timeslotEnabled = isTimeslotEnabled();

	const handleDateChange = (date) => {
		setDate(date);
		jQuery('.iconic-wds-tooltip').hide();

		if (date) {
			const ymd = format(date, 'yyyyMMdd');
			setDateYmd( type, ymd );
			
			if ( timeslotEnabled ) {
				fetchTimeslots( ymd );
			}
		}
	}

	const handleTimeslotChange = (e) => {
		setTimeslot( type, e.target.value );
	};

	const fetchTimeslots = useCallback(async (ymd) => {
		setLoadingTimeslots(true);
		setTimeslotOptions([{ value: '', formatted_with_fee: 'Loading...' }]);

		try {
			const response = await fetchTimeslotsOnDate(ymd, shippingMethod, 'subscription');
			let slots = [{ value: '', formatted_with_fee: 'Select a timeslot' }];

			if (response?.data?.timeslot) {
				slots = [...slots, ...response.data.timeslot];
			}

			setTimeslotOptions(slots);
			setLoadingTimeslots(false);
		} catch (error) {
			console.error('Error fetching timeslots:', error);
			setLoadingTimeslots(false);
		}
	}, [shippingMethod]);

	/**
	 * Populate the values of the reserved slot.
	 */
	useEffect(() => {
		const populateReservedSlot = async () => { 
			if (reservedSlot?.date_ymd) {
				const dateObj = dateYmdToDate(reservedSlot.date_ymd);
				if (dateObj) {
					setDate(dateObj);
					setDateYmd(type, reservedSlot.date_ymd);
					await fetchTimeslots(reservedSlot.date_ymd);
					setTimeslot(type, reservedSlot.timeslot_value);
				}
			}
		}

		populateReservedSlot();
	}, [reservedSlot]);
	

	return (
		<div>
			<div className="wds-fieldbox-selectbox">
				<Header type={type} />

				<div className="wds-fieldbox-selectbox__body">
					<div className="wds-fieldbox-selectbox__body-left">
						<div className="wds-fieldbox-selectbox__body-title">
							{
								'delivery' === shippingMethodLabelType ?
									__('Select a delivery day', 'jckwds') :
									__('Select a pickup day', 'jckwds')
							}
						</div>
						<div className="wds-fieldbox-selectbox__body-subtitle">
							{subscriptionData?.formatted_interval}
						</div>
					</div>

					<div className="wds-fieldbox-selectbox__body-right">
						<WdsDatePicker
							bookableDates={bookableDates[type]}
							selected={date}
							handleDateChange={handleDateChange}
							maxDate={getMaxDate()}
							id='iconic-wds-subscription-date'
						/>
					</div>
				</div>
				
				{(date && timeslotEnabled) &&
					<div className="wds-fieldbox-selectbox__body">
						<div className="wds-fieldbox-selectbox__body-left">
							<div className="wds-fieldbox-selectbox__body-title">
								{__('Select Timeslot', 'jckwds')}
							</div>
						</div>

						<div className="wds-fieldbox-selectbox__body-right">
							<div className="wds-fieldbox-selectbox__timeslot wds-block-field-wrapper wds-block-field-wrapper--time">
								<select
									value={timeslot}
									onChange={handleTimeslotChange}
									disabled={loadingTimeslots}
									id='iconic-wds-subscription-timeslot'
								>
									{timeslotOptions.map((slot) => (
										<option key={slot.value} value={slot.value}>
											{decodeEntities(slot.formatted_with_fee)}
										</option>
									))}
								</select>
							</div>
						</div>
					</div>
				}

			</div>

			{SubscriptionProductType.SUBSCRIPTION === type && date && <Alert>{sprintf(__('First delivery on %s', 'jckwds'), format(date, dateFormat))}</Alert>}
		</div>
	);
};

const Header = ( {type} ) => {
	const subscriptionData = useSelect(select => select(STORE_NAME).getSubscriptionData());
	const formattedSubscriptionInterval = useSelect(select => select(STORE_NAME).getFormattedSubscriptionInterval());

	let text = SubscriptionProductType.SUBSCRIPTION === type
		? __('Select timeslot for the subscription products in your cart.', 'jckwds')
		: __('Select timeslot for the one-time purchase products in your cart.', 'jckwds');

	if (SubscriptionProductType.SUBSCRIPTION === type && formattedSubscriptionInterval) {
		text = formattedSubscriptionInterval;
	}

	return (
		<div className="wds-fieldbox-selectbox__header">
			<span className="wds-fieldbox-selectbox__header-title">
				{createInterpolateElement(text, {
					strong: <strong />,
				})}
			</span>
		</div>
	);
};


export default SubscriptionMonthlySelector;